import type { BarChartBar } from '../components/bar-chart.js';
import type { AiUsageSummary } from '../data/api.js';
import { formatCurrencyText } from './currency-format.js';

/** Label every this-many days, counting back from the newest bar (HS2-GH14FR). */
export const AI_USAGE_LABEL_EVERY = 7;

/** AI spend amounts on the AI Usage & Budget page always show cents (HS2-AC1W3K). */
export const AI_USAGE_FRACTION_DIGITS = 2;

/** A `YYYY-MM-DD` calendar date as a local Date (no UTC shift), or null when malformed. */
function calendarDate(date: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * A readable label for an AI usage `YYYY-MM-DD` day, in the viewer's locale — `Mon, Oct 12`, or
 * `Mon, Oct 12, 2026` with `year`. A malformed date is returned unchanged (HS2-AC1W3K).
 */
export function formatAiUsageDate(date: string, { year = false }: { year?: boolean } = {}): string {
  return (
    calendarDate(date)?.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(year ? { year: 'numeric' } : {}),
    }) ?? date
  );
}

/** USD text for an AI spend amount, always to the cent (HS2-AC1W3K). */
export function formatAiUsageUsd(usd: number): string {
  return formatCurrencyText(usd, 'USD', AI_USAGE_FRACTION_DIGITS);
}

/**
 * Bars for the AI usage last-30-days chart. The server already sends one entry per
 * calendar day in the org timezone, zero days included, oldest first. Axis labels are
 * sparse — the newest day and every seventh day before it — so a month fits a phone
 * width; every bar keeps a tooltip with its full date and amount.
 */
export function aiUsageDayBars(days: readonly { date: string; usd: number }[]): BarChartBar[] {
  return days.map((day, index) => {
    const parsed = calendarDate(day.date);
    const fromNewest = days.length - 1 - index;
    const short = parsed?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) ?? day.date;
    return {
      key: day.date,
      value: day.usd,
      label: fromNewest % AI_USAGE_LABEL_EVERY === 0 ? short : '',
      title: `${formatAiUsageDate(day.date, { year: true })}: ${formatAiUsageUsd(day.usd)}`,
    };
  });
}

const ANALYSIS_RUN_NAMES: Record<string, string> = { pricing: 'Pricing check', scope: 'Scope evaluation' };

/**
 * The By analysis row label: the demand the run analysed, then the run and its version, e.g.
 * `Caustic soda pump maintenance · Pricing check v2`. A row whose analysis is gone reads
 * `Deleted analysis` rather than exposing the raw id (HS2-BR3VAG).
 */
export function aiUsageAnalysisLabel(spend: AiUsageSummary['byAnalysis'][number]): string {
  if (spend.analysisType === null) return 'Deleted analysis';
  const run = ANALYSIS_RUN_NAMES[spend.analysisType] ?? spend.analysisType;
  const versioned = spend.version === null ? run : `${run} v${String(spend.version)}`;
  return `${spend.demandTitle ?? 'Deleted demand'} · ${versioned}`;
}
